// Shared sign-in layout and controls. All colors come from modal-theme.css.
export function SignInModalStyles() {
  return (
    <style jsx global>{`
      .whop-login-page {
        position: relative;
        display: flex;
        min-height: 100vh;
        min-height: 100dvh;
        align-items: center;
        justify-content: center;
        padding: 16px 16px max(16px, 4vh);
        background: var(--twt-modal-page);
        color: var(--twt-modal-text);
        color-scheme: var(--twt-modal-color-scheme);
        font-family: var(--release-sans);
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
        font-optical-sizing: auto;
      }

      /* The ivory page is one flat color: no haze, no gradient. */
      .whop-login-page::before {
        display: none;
      }

      .twt-signin-modal ::selection {
        background: color-mix(in srgb, var(--twt-copper) 24%, transparent);
        color: var(--twt-ink, #141413);
      }

      .whop-login-stage {
        position: relative;
        width: 100%;
        display: flex;
        justify-content: center;
      }

      .whop-login-modal {
        position: relative;
        display: flex;
        width: 100%;
        max-width: 480px;
        flex-direction: column;
        gap: 24px;
        padding: 44px 40px 32px;
        border: 0;
        border-radius: 20px;
        background: var(--twt-card, #fff);
        box-shadow: 0 1px 2px rgba(20, 20, 19, 0.04),
          0 24px 56px -24px rgba(54, 40, 28, 0.24);
        text-align: center;
        animation: twt-modal-fade 50ms cubic-bezier(0.16, 1, 0.3, 1) both,
          twt-modal-rise 300ms cubic-bezier(0.32, 0.72, 0, 1) both;
      }

      @keyframes twt-modal-fade {
        from {
          opacity: 0;
        }
      }

      @keyframes twt-modal-rise {
        from {
          transform: translateY(8px) scale(0.97);
        }
      }

      .whop-login-modal-head {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
      }

      .whop-login-icons {
        display: inline-flex;
        align-items: center;
        flex: 0 0 auto;
      }

      .whop-login-whop-mark,
      .whop-login-twt-mark {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: auto;
        height: auto;
        background: none;
      }

      .whop-login-whop-mark {
        width: 36px;
        height: 36px;
        color: var(--twt-ink, #141413);
      }

      .whop-login-whop-mark svg {
        width: 36px;
        height: 36px;
      }

      /* The headline, in the site's serif, two lines wide on the panel. */
      .whop-login-title {
        margin: 0;
        max-width: 11em;
        color: var(--twt-espresso);
        font-family: var(--release-serif), Georgia, serif;
        font-size: 30px;
        font-weight: 500;
        line-height: 1.15;
        letter-spacing: -0.01em;
        text-wrap: balance;
      }

      .whop-login-title strong {
        color: inherit;
        font-weight: inherit;
      }

      /* The quiet line under the headline (the code step's "Check your email
         for a one-time passcode."). */
      .whop-login-subtext {
        margin: -8px 0 0;
        color: var(--twt-graphite, #6a6a62);
        font-size: 15px;
        line-height: 1.5;
        text-wrap: pretty;
      }

      /* While a sign-in finishes the panel shows its own heading, which
         takes the headline's place. */
      .whop-login-modal:has(.twt-signin-heading) .whop-login-title,
      .whop-login-modal:has(.twt-signin-heading) .whop-login-subtext {
        display: none;
      }

      .whop-login-not-member {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: -8px 0 0;
        padding: 10px 12px;
        border-radius: 10px;
        background: var(--twt-oxblood-bg, #fff0ed);
        color: var(--twt-oxblood-deep, #8a2f1f);
        font-size: 14px;
        line-height: 1.5;
        text-align: left;
      }

      .whop-login-not-member a {
        color: inherit;
        font-weight: 500;
        text-decoration: underline;
        text-underline-offset: 3px;
      }

      /* The lines under the form, in the quiet ink: the terms line, as in
         the Whop modal, and on the wall the way to an account. */
      .whop-login-footnote,
      .twt-signin-terms {
        margin: 0;
        color: var(--twt-graphite, #6a6a62);
        line-height: 1.5;
        text-align: center;
      }

      .twt-signin-terms {
        font-size: 14px;
        text-wrap: balance;
      }

      .twt-signin-terms a {
        color: var(--twt-ink-soft, #2a2a26);
        text-decoration: underline;
        text-decoration-color: color-mix(
          in srgb,
          currentColor 35%,
          transparent
        );
        text-underline-offset: 3px;
        transition: text-decoration-color 150ms ease;
      }

      .twt-signin-terms a:hover {
        text-decoration-color: currentColor;
      }

      .twt-signin-terms a:focus-visible {
        border-radius: 4px;
        outline: 2px solid var(--twt-copper);
        outline-offset: 3px;
      }

      .whop-login-footnote {
        font-size: 14px;
      }

      /* The way to an account, in copper. */
      .whop-login-footnote a {
        color: var(--twt-copper);
        font-weight: 500;
        text-decoration: underline;
        text-decoration-color: color-mix(
          in srgb,
          var(--twt-copper) 40%,
          transparent
        );
        text-underline-offset: 3px;
        transition: color 150ms ease, text-decoration-color 150ms ease;
      }

      .whop-login-footnote a:hover {
        color: var(--twt-espresso);
        text-decoration-color: currentColor;
      }

      .whop-login-footnote a:focus-visible {
        border-radius: 4px;
        outline: 2px solid var(--twt-copper);
        outline-offset: 3px;
      }

      /* The site's tokens (globals.css :root) carry the modal as they are.
         Espresso (the headline) and copper (the focus line) are the two this
         design adds; the form's own focus rules read the accent through
         --twt-clay, so that reads copper here. */
      .twt-signin-modal {
        --twt-espresso: #36281c;
        --twt-copper: #bf7550;
        --twt-clay: var(--twt-copper);
        color-scheme: light;
      }

      .twt-visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        white-space: nowrap;
        border: 0;
      }

      /* The step change: the frame animates its height and the entering content
         fades in from the side it came from. 150ms, ease-out. */
      /* Not positioned on purpose: the back arrow inside a step is placed against
         the modal panel, not against this frame. */
      .twt-step-frame {
        width: 100%;
        transition: height 150ms cubic-bezier(0.16, 1, 0.3, 1);
      }

      .twt-step-frame.is-animating {
        overflow: hidden;
      }

      /* Where a step's back arrow renders, outside the animated content box
         (SignInStepFrame.tsx): no box of its own, so the arrow is positioned
         against the modal panel throughout the step change. */
      .twt-step-back-control {
        display: contents;
      }

      .twt-step-content {
        --twt-step-shift: 12px;
      }

      .twt-step-content[data-direction="back"] {
        --twt-step-shift: -12px;
      }

      .twt-step-content.is-entering {
        animation: twt-step-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
      }

      @keyframes twt-step-in {
        from {
          opacity: 0;
          transform: translateX(var(--twt-step-shift));
        }
      }

      /* The spinner inside a pressed button, and the larger one while the saved
         account is looked up. */
      .twt-spin {
        display: inline-block;
        width: 16px;
        height: 16px;
        flex: 0 0 auto;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        opacity: 0.85;
        animation: twt-spin 700ms linear infinite;
      }

      .twt-spin-lg {
        width: 24px;
        height: 24px;
        color: var(--twt-graphite, #6a6a62);
      }

      @keyframes twt-spin {
        to {
          transform: rotate(360deg);
        }
      }

      .twt-signin-modal .whop-oauth-lookup {
        display: flex;
        justify-content: center;
        padding: 24px 0;
      }

      .twt-signin-modal .twt-signin {
        max-width: none;
        gap: 24px;
        color: var(--twt-ink-soft, #2a2a26);
      }

      .twt-signin-modal .twt-signin-notice,
      .twt-signin-modal .twt-signin-status {
        margin: 0;
        color: var(--twt-ink-soft, #2a2a26);
        font-size: 14px;
        line-height: 1.5;
        text-align: center;
      }

      /* The panel's own heading appears only while a sign-in finishes. */
      .twt-signin-modal .twt-signin-heading {
        margin: 0;
        color: var(--twt-espresso);
        font-family: var(--release-serif), Georgia, serif;
        font-size: 22px;
        font-weight: 500;
        line-height: 1.3;
        letter-spacing: 0;
        text-align: center;
      }

      .twt-signin-modal .whop-oauth-form {
        gap: 12px;
      }

      .twt-signin-modal .whop-oauth-secondary {
        gap: 8px;
        text-align: left;
      }

      /* The "Email" label over the field, as in the Whop modal: its light
         gray at the regular weight. (The panel's own rule for it,
         TwtSignInPanel.tsx, is matched by the second selector.) */
      .twt-signin-modal .whop-oauth-email-label,
      .twt-signin-modal .twt-signin .whop-oauth-email-label {
        display: block;
        margin: 0;
        color: var(--twt-modal-label);
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        text-align: left;
      }

      .twt-signin-modal .whop-oauth-email-wrap {
        position: relative;
      }

      /* One plain field, as designed: no mark inside it. */
      .twt-signin-modal .whop-oauth-email-icon {
        display: none;
      }

      .twt-signin-modal .whop-oauth-email-field {
        min-height: 56px;
        border: 0;
        border-radius: 14px;
        background: var(--twt-card, #fff);
        box-shadow: inset 0 0 0 1px var(--twt-hairline, #e8e6dc);
        padding: 0 16px;
        color: var(--twt-ink, #141413);
        font: inherit;
        font-size: 16px;
        letter-spacing: -0.005em;
        transition: box-shadow 150ms ease;
      }

      .twt-signin-modal .whop-oauth-email-field::placeholder {
        color: var(--twt-graphite, #6a6a62);
      }

      /* The copper focus line. */
      .twt-signin-modal .whop-oauth-email-field:focus {
        box-shadow: inset 0 0 0 1.5px var(--twt-copper),
          0 0 0 4px color-mix(in srgb, var(--twt-copper) 14%, transparent);
        outline: none;
      }

      .twt-signin-modal .whop-oauth-email-field[aria-invalid="true"] {
        box-shadow: inset 0 0 0 1.5px var(--twt-oxblood, #c4432f);
      }

      /* Continue: charcoal, full width, 56px on a 14px corner, ready at rest
         as in the Whop modal (an empty or partial address gets a message on
         press). The Cancel link home is hidden here. */
      .twt-signin-modal .whop-oauth-form .whop-oauth-actions {
        flex-direction: column;
        gap: 0;
        margin-top: 0;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-cancel {
        display: none;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-approve {
        flex: none;
        width: 100%;
      }

      .twt-signin-modal .whop-oauth-approve,
      .twt-signin-modal .whop-oauth-primary,
      .twt-signin-modal .twt-signin-primary {
        display: inline-flex;
        min-height: 56px;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 0 24px;
        border: 0;
        border-radius: 14px;
        background: var(--twt-ink-soft, #2a2a26);
        color: var(--twt-card, #fff);
        font: inherit;
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
        letter-spacing: -0.01em;
        transition: background-color 150ms ease, transform 150ms ease;
      }

      .twt-signin-modal .whop-oauth-approve:hover:not(:disabled),
      .twt-signin-modal .whop-oauth-primary:hover:not(:disabled),
      .twt-signin-modal .twt-signin-primary:hover:not(:disabled) {
        background: var(--twt-ink, #141413);
      }

      .twt-signin-modal .whop-oauth-approve:active:not(:disabled),
      .twt-signin-modal .whop-oauth-primary:active:not(:disabled),
      .twt-signin-modal .twt-signin-primary:active:not(:disabled) {
        background: var(--twt-ink, #141413);
        transform: scale(0.99);
      }

      /* Disabled (a request is running): the same shape in a muted warm gray. */
      .twt-signin-modal .whop-oauth-approve:disabled,
      .twt-signin-modal .whop-oauth-primary:disabled,
      .twt-signin-modal .twt-signin-primary:disabled {
        background: color-mix(
          in srgb,
          var(--twt-ink-soft, #2a2a26) 48%,
          var(--twt-card, #fff)
        );
        color: var(--twt-card, #fff);
        cursor: not-allowed;
      }

      .twt-signin-modal .whop-oauth-approve[aria-busy="true"],
      .twt-signin-modal .whop-oauth-primary[aria-busy="true"] {
        cursor: progress;
      }

      /* Google, the passkey, and Discord: the row of icon-only buttons under
         the "or" divider, in the Whop modal's order (Google, Apple, Discord).
         Three equal buttons across the panel, 52px tall on a 12px corner,
         12px apart, the mark alone in the middle at the canvas's size:
         Google and the key on graphite, Discord on the right on its own
         blurple with its mark in white. */
      .twt-signin-modal .twt-signin-providers {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: center;
        gap: 12px;
      }

      .twt-signin-modal .twt-signin-secondary {
        position: relative;
        display: inline-flex;
        min-height: 48px;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 0 16px;
        border: 0;
        border-radius: 12px;
        background: var(--twt-card, #fff);
        box-shadow: inset 0 0 0 1px var(--twt-hairline, #e8e6dc);
        color: var(--twt-ink, #141413);
        font: inherit;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: -0.005em;
        white-space: nowrap;
        transition: background-color 150ms ease, box-shadow 150ms ease;
      }

      .twt-signin-modal
        .twt-signin-providers
        > .twt-signin-secondary:not(.twt-signin-icon-button) {
        flex: 1 1 0;
        min-width: 0;
        min-height: 44px;
        padding: 0;
        gap: 0;
      }

      .twt-signin-modal .twt-signin-icon-button {
        flex: 1 1 0;
        width: auto;
        min-width: 0;
        height: 52px;
        min-height: 52px;
        padding: 0;
        gap: 0;
      }

      .twt-signin-modal .twt-signin-secondary:hover:not(:disabled) {
        background: var(--twt-canvas, #faf9f5);
        box-shadow: inset 0 0 0 1px var(--twt-graphite-soft, #8a8980);
      }

      .twt-signin-modal .twt-signin-secondary:active:not(:disabled) {
        background: var(--twt-ground, #f3f1e7);
      }

      .twt-signin-modal .twt-signin-secondary:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .twt-signin-modal .twt-signin-icon-button {
        background: var(--twt-modal-provider-tile);
        box-shadow: none;
        color: var(--twt-modal-service-white);
      }

      .twt-signin-modal .twt-signin-icon-button:hover:not(:disabled) {
        background: var(--twt-modal-provider-hover);
        box-shadow: none;
      }

      .twt-signin-modal .twt-signin-icon-button:active:not(:disabled) {
        background: var(--twt-modal-provider-active);
      }

      .twt-signin-modal .twt-signin-icon-button.twt-signin-discord {
        background: var(--twt-modal-discord);
      }

      .twt-signin-modal
        .twt-signin-icon-button.twt-signin-discord:hover:not(:disabled) {
        background: var(--twt-modal-discord-hover);
      }

      .twt-signin-modal
        .twt-signin-icon-button.twt-signin-discord:active:not(:disabled) {
        background: var(--twt-modal-discord-active);
      }

      .twt-signin-modal .twt-signin-discord > svg {
        fill: var(--twt-modal-service-white);
      }

      /* Disabled (while Clerk loads, or while another way in runs), a button
         keeps its fill and its full mark, so the row never looks faded. */
      .twt-signin-modal .twt-signin-icon-button:disabled {
        opacity: 1;
      }

      .twt-signin-modal .twt-signin-button-icon,
      .twt-signin-modal .twt-signin-secondary .twt-spin {
        position: static;
        flex: 0 0 auto;
        transform: none;
      }

      .twt-signin-modal .twt-signin-secondary > span {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* The "or" between Continue and the row of icons, set as in the Whop
         modal: small capitals between two hairlines. */
      .twt-signin-modal .twt-signin-divider {
        display: flex;
        align-items: center;
        gap: 14px;
        margin: 0;
        color: var(--twt-graphite, #6a6a62);
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      .twt-signin-modal .twt-signin-divider::before,
      .twt-signin-modal .twt-signin-divider::after {
        content: "";
        height: 1px;
        flex: 1;
        background: var(--twt-hairline, #e8e6dc);
      }

      /* The quiet, centered text buttons: Try again's neighbours, Not you?. */
      .twt-signin-modal .twt-signin-back,
      .twt-signin-modal .whop-oauth-ghost {
        align-self: center;
        min-height: 40px;
        padding: 8px 12px;
        border: 0;
        border-radius: 10px;
        background: none;
        color: var(--twt-graphite, #6a6a62);
        font: inherit;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.4;
        text-decoration: none;
        transition: color 150ms ease, background-color 150ms ease;
      }

      .twt-signin-modal .twt-signin-back:hover:not(:disabled),
      .twt-signin-modal .whop-oauth-ghost:hover:not(:disabled) {
        color: var(--twt-ink, #141413);
        background: var(--twt-canvas, #faf9f5);
      }

      .twt-signin-modal .twt-signin-back:disabled,
      .twt-signin-modal .whop-oauth-ghost:disabled {
        color: var(--twt-graphite-soft, #8a8980);
        opacity: 0.7;
        cursor: not-allowed;
      }

      .twt-signin-modal .twt-signin-row {
        justify-content: center;
        gap: 8px 24px;
      }

      /* The back arrow at the modal's top left. */
      .twt-signin-modal .twt-modal-back {
        position: absolute;
        top: 14px;
        left: 14px;
        display: inline-flex;
        width: 40px;
        height: 40px;
        min-height: 0;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0;
        border: 0;
        border-radius: 10px;
        background: none;
        color: var(--twt-graphite, #6a6a62);
        font: inherit;
        cursor: pointer;
        transition: color 150ms ease, background-color 150ms ease;
      }

      .twt-signin-modal .twt-modal-back:hover:not(:disabled) {
        color: var(--twt-ink, #141413);
        background: var(--twt-canvas, #faf9f5);
        text-decoration: none;
      }

      .twt-signin-modal .twt-modal-back:disabled {
        color: var(--twt-graphite-soft, #8a8980);
        cursor: not-allowed;
      }

      .twt-signin-modal .twt-signin-primary:focus-visible,
      .twt-signin-modal .twt-signin-secondary:focus-visible,
      .twt-signin-modal .twt-signin-back:focus-visible,
      .twt-signin-modal .twt-modal-back:focus-visible,
      .twt-signin-modal .whop-oauth-form .whop-oauth-approve:focus-visible,
      .twt-signin-modal .whop-oauth-primary:focus-visible,
      .twt-signin-modal .whop-oauth-form .whop-oauth-cancel:focus-visible,
      .twt-signin-modal .whop-oauth-form .whop-oauth-switch:focus-visible,
      .twt-signin-modal .whop-oauth-form .whop-oauth-resend-btn:focus-visible,
      .twt-signin-modal .whop-oauth-ghost:focus-visible {
        border-radius: inherit;
        outline: 2px solid var(--twt-copper);
        outline-offset: 3px;
      }

      .twt-signin-modal .twt-signin-back:focus-visible,
      .twt-signin-modal .whop-oauth-form .whop-oauth-resend-btn:focus-visible,
      .twt-signin-modal .whop-oauth-ghost:focus-visible {
        border-radius: 6px;
      }

      /* Errors: the site's oxblood in a quiet band with its mark. */
      .twt-signin-modal .twt-signin-error,
      .twt-signin-modal .whop-oauth-form .whop-oauth-error,
      .twt-signin-modal .whop-oauth-error {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 0;
        padding: 10px 12px;
        border-radius: 10px;
        background: var(--twt-oxblood-bg, #fff0ed);
        color: var(--twt-oxblood-deep, #8a2f1f);
        font-size: 14px;
        font-weight: 500;
        line-height: 1.5;
        text-align: left;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-error > svg,
      .twt-signin-modal .whop-oauth-error > svg {
        flex: 0 0 auto;
        margin-top: 3px;
      }

      /* The code step: the line about where the code went, six 50px cells,
         and the resend line under them. */
      .twt-signin-modal .whop-oauth-form.whop-oauth-code-form {
        max-width: none;
        gap: 16px;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-code-head {
        align-items: center;
        gap: 6px;
        text-align: center;
      }

      .twt-signin-modal .whop-oauth-code-head .whop-oauth-code-sentto {
        color: var(--twt-ink-soft, #2a2a26);
        font-size: 16px;
        line-height: 1.5;
      }

      .twt-signin-modal .whop-oauth-code-head .whop-oauth-code-sentto span {
        color: var(--twt-ink, #141413);
        font-weight: 600;
      }

      .twt-signin-modal .whop-oauth-code-head .whop-oauth-code-help {
        max-width: 34em;
        color: var(--twt-graphite, #6a6a62);
        font-size: 13px;
        line-height: 1.5;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-code-wrap {
        margin-top: 4px;
      }

      .twt-signin-modal .whop-oauth-code-wrap.is-shaking {
        animation: twt-shake 320ms cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
      }

      @keyframes twt-shake {
        10%,
        90% {
          transform: translateX(-2px);
        }
        20%,
        80% {
          transform: translateX(4px);
        }
        30%,
        50%,
        70% {
          transform: translateX(-6px);
        }
        40%,
        60% {
          transform: translateX(6px);
        }
      }

      /* The cells' design goes in through the variables the form's scoped rules
         read: a 50px cell on a 10px corner with the hairline as an inset ring,
         the copper as the caret ring, the error tone when the code was refused. */
      .twt-signin-modal .whop-oauth-code-wrap {
        --twt-code-cell-gap: 8px;
        --twt-code-cell-height: 50px;
        --twt-code-cell-radius: 10px;
        --twt-code-cell-font-size: 22px;
        --twt-code-cell-filled-border: transparent;
        --twt-code-cell-filled-bg: var(--twt-canvas, #faf9f5);
        --twt-code-cell-shadow: inset 0 0 0 1px var(--twt-hairline, #e8e6dc);
        --twt-code-cell-focus-border: transparent;
        --twt-code-cell-focus-shadow: inset 0 0 0 1.5px var(--twt-copper),
          0 0 0 4px color-mix(in srgb, var(--twt-copper) 14%, transparent);
        --twt-code-cell-invalid-border: transparent;
        --twt-code-cell-invalid-shadow: inset 0 0 0 1px
          var(--twt-oxblood, #c4432f);
        --twt-code-cell-invalid-focus-border: transparent;
        --twt-code-cell-invalid-focus-shadow: inset 0 0 0 1.5px
          var(--twt-oxblood, #c4432f);
      }

      .twt-signin-modal
        .whop-oauth-form
        .whop-oauth-code-wrap
        .whop-oauth-code-grid
        div {
        border: 0;
        font-family: inherit;
        font-weight: 600;
      }

      .twt-signin-modal .whop-oauth-code-input:disabled {
        cursor: progress;
      }

      /* The request's progress under the cells (the sixth digit submits; there
         is no button), then the resend line. */
      .twt-signin-modal .whop-oauth-form .whop-oauth-code-status {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin: -4px 0 -4px;
        color: var(--twt-ink-soft, #2a2a26);
        font-size: 14px;
        line-height: 1.5;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-resend {
        margin: 0;
        color: var(--twt-graphite, #6a6a62);
        font-size: 14px;
        line-height: 1.5;
        text-align: center;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-resend-btn {
        color: var(--twt-ink, #141413);
        font: inherit;
        font-size: 14px;
        font-weight: 500;
        text-decoration: underline;
        text-decoration-color: var(--twt-hairline, #e8e6dc);
        text-underline-offset: 3px;
        transition: color 150ms ease, text-decoration-color 150ms ease;
      }

      .twt-signin-modal
        .whop-oauth-form
        .whop-oauth-resend-btn:hover:not(:disabled) {
        color: var(--twt-copper);
        text-decoration-color: currentColor;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-resend-btn:disabled {
        color: var(--twt-graphite-soft, #8a8980);
        font-variant-numeric: tabular-nums;
        text-decoration: none;
        cursor: default;
      }

      .twt-signin-modal .whop-oauth-form .whop-oauth-resend-ok {
        display: block;
        margin: 4px 0 0;
        color: var(--twt-sage-deep, #475a32);
      }

      /* The saved-account card the form can open with. */
      .twt-signin-modal .whop-oauth-prefill-card {
        border: 0;
        border-radius: 12px;
        background: var(--twt-canvas, #faf9f5);
        box-shadow: inset 0 0 0 1px var(--twt-hairline, #e8e6dc);
        text-align: left;
      }

      .twt-signin-modal .whop-oauth-prefill-avatar {
        background-color: var(--twt-espresso);
        color: var(--twt-card, #fff);
      }

      .twt-signin-modal .whop-oauth-prefill-card .whop-oauth-prefill-name {
        color: var(--twt-ink, #141413);
      }

      .twt-signin-modal .whop-oauth-prefill-card .whop-oauth-prefill-handle {
        color: var(--twt-graphite, #6a6a62);
      }

      /* Phones: the panel is the viewport less 16px each side with 24px
         inside; the three icon buttons keep their row; the cells shrink with
         the grid. 16px in the field so iOS never zooms on focus. */
      @media (max-width: 480px) {
        .whop-login-modal {
          padding: 32px 24px 24px;
          border-radius: 18px;
        }

        .whop-login-modal-head {
          gap: 16px;
        }

        .whop-login-title {
          font-size: 27px;
        }

        .whop-login-subtext {
          margin-top: -4px;
          font-size: 14px;
        }

        .twt-signin-modal .whop-oauth-code-wrap {
          --twt-code-cell-gap: 6px;
          --twt-code-cell-height: 46px;
          --twt-code-cell-font-size: 20px;
        }
      }

      /* Modal colors come from the shared palette. Service buttons keep
         their own brand colors. */
      .twt-signin-modal {
        --twt-card: var(--twt-modal-panel);
        --twt-canvas: var(--twt-modal-field);
        --twt-ground: var(--twt-modal-hover);
        --twt-ink: var(--twt-modal-text);
        --twt-ink-soft: var(--twt-modal-text);
        --twt-espresso: var(--twt-modal-text);
        --twt-copper: var(--twt-modal-accent-text);
        --twt-graphite: var(--twt-modal-muted);
        --twt-graphite-soft: var(--twt-modal-faint);
        --twt-hairline: var(--twt-modal-border);
        --twt-oxblood: var(--twt-modal-error-border);
        --twt-oxblood-deep: var(--twt-modal-error);
        --twt-oxblood-bg: var(--twt-modal-selected);
        --twt-sage-deep: var(--twt-modal-success);
        color: var(--twt-modal-text);
        color-scheme: var(--twt-modal-color-scheme);
      }

      .twt-signin-modal ::selection {
        background: color-mix(in srgb, var(--twt-copper) 40%, transparent);
        color: var(--twt-modal-text);
      }

      .whop-login-modal.twt-signin-modal {
        border: 1px solid var(--twt-modal-border);
        box-shadow: 0 28px 64px -28px var(--twt-modal-shadow);
      }

      /* The headline as the Whop modal and the graphite mock set it: the
         interface sans, semibold. */
      .twt-signin-modal .whop-login-title {
        color: var(--twt-modal-text);
        font-family: var(--twt-modal-heading-font);
        font-size: 28px;
        font-weight: 600;
        letter-spacing: -0.015em;
        line-height: 36px;
      }

      .twt-signin-modal .whop-oauth-email-label,
      .twt-signin-modal .twt-signin .whop-oauth-email-label {
        color: var(--twt-modal-label);
      }

      .twt-signin-modal .whop-oauth-email-field {
        background: var(--twt-modal-field);
        box-shadow: inset 0 0 0 1px var(--twt-modal-border);
        color: var(--twt-modal-text);
      }

      .twt-signin-modal .whop-oauth-email-field::placeholder {
        color: var(--twt-modal-placeholder);
      }

      /* Continue: the newsletter window's light primary on graphite. */
      .twt-signin-modal .whop-oauth-approve,
      .twt-signin-modal .whop-oauth-primary,
      .twt-signin-modal .twt-signin-primary {
        background: var(--twt-modal-primary);
        color: var(--twt-modal-primary-text);
      }

      .twt-signin-modal .whop-oauth-approve:hover:not(:disabled),
      .twt-signin-modal .whop-oauth-primary:hover:not(:disabled),
      .twt-signin-modal .twt-signin-primary:hover:not(:disabled) {
        background: var(--twt-modal-primary-hover);
      }

      .twt-signin-modal .whop-oauth-approve:active:not(:disabled),
      .twt-signin-modal .whop-oauth-primary:active:not(:disabled),
      .twt-signin-modal .twt-signin-primary:active:not(:disabled) {
        background: var(--twt-modal-primary-active);
      }

      .twt-signin-modal .whop-oauth-approve:disabled,
      .twt-signin-modal .whop-oauth-primary:disabled,
      .twt-signin-modal .twt-signin-primary:disabled {
        background: var(--twt-modal-primary-disabled);
        color: var(--twt-modal-primary-text);
      }

      .twt-signin-modal .twt-signin-icon-button {
        background: var(--twt-modal-provider-tile);
        color: var(--twt-modal-provider-text);
        box-shadow: inset 0 0 0 1px var(--twt-modal-provider-border);
      }

      .twt-signin-modal .twt-signin-icon-button:hover:not(:disabled) {
        background: var(--twt-modal-provider-hover);
        box-shadow: inset 0 0 0 1px var(--twt-modal-provider-border);
      }

      .twt-signin-modal .twt-signin-icon-button:active:not(:disabled) {
        background: var(--twt-modal-provider-active);
      }

      .twt-signin-modal .twt-signin-icon-button.twt-signin-discord {
        box-shadow: none;
        color: var(--twt-modal-service-white);
      }

      .twt-signin-modal .twt-signin-divider {
        color: var(--twt-modal-label);
        gap: 16px;
      }

      .twt-signin-modal .twt-signin-divider::before,
      .twt-signin-modal .twt-signin-divider::after {
        background: var(--twt-modal-divider);
      }

      .twt-signin-screen {
        position: relative;
        container-type: inline-size;
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 480px;
        gap: 24px;
        padding: 44px 40px 32px;
        border-radius: 20px;
        background: var(--twt-modal-panel);
        font-family: var(--release-sans);
        text-align: center;
      }

      .whop-login-stage > .twt-signin-screen {
        border: 1px solid var(--twt-modal-panel-border);
      }

      .twt-signin-screen-head {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
      }

      .twt-signin-screen .twt-signin-brand {
        gap: 13px;
        white-space: nowrap;
      }

      .twt-signin-screen .twt-signin-brand-mark {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        background: var(--twt-modal-brand-mark);
        color: var(--twt-modal-brand-mark-text);
        font-size: 26px;
        line-height: 1;
      }

      .twt-signin-screen .twt-signin-brand-word {
        color: var(--twt-modal-text);
        font-size: 32px;
        line-height: 1;
      }

      .twt-signin-screen .twt-signin-brand-emphasis {
        color: var(--twt-modal-brand-emphasis);
      }

      .twt-signin-screen .whop-login-title {
        max-width: none;
      }

      .twt-signin-screen .twt-signin .whop-oauth-form {
        gap: 24px;
        margin: 0;
      }

      .twt-signin-screen .twt-signin-status:empty {
        display: none;
      }

      .twt-signin-screen:has(.twt-signin-heading) .whop-login-title {
        display: none;
      }

      .twt-signin-screen .twt-signin-terms {
        margin: 0;
        color: var(--twt-modal-label);
        font-size: clamp(8px, 3.25cqi, 13px);
        line-height: 20px;
        letter-spacing: -0.1px;
        white-space: nowrap;
      }

      .twt-signin-screen .twt-signin-terms a {
        color: var(--twt-modal-link);
        text-decoration-color: currentColor;
      }

      @media (max-width: 520px) {
        .twt-signin-screen {
          padding: 32px 20px 28px;
        }

        .twt-signin-screen .twt-signin-brand {
          gap: 10px;
        }

        .twt-signin-screen .twt-signin-brand-mark {
          width: 36px;
          height: 36px;
          font-size: 23px;
          border-radius: 8px;
        }

        .twt-signin-screen .twt-signin-brand-word {
          font-size: 28px;
        }

        .twt-signin-screen .whop-login-title {
          font-size: 26px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .whop-login-modal,
        .twt-step-content.is-entering,
        .twt-signin-modal .whop-oauth-code-wrap.is-shaking {
          animation: none;
        }

        .twt-step-frame,
        .twt-signin-modal .whop-oauth-email-field,
        .twt-signin-modal .whop-oauth-approve,
        .twt-signin-modal .whop-oauth-primary,
        .twt-signin-modal .twt-signin-primary,
        .twt-signin-modal .twt-signin-secondary,
        .twt-signin-modal .twt-signin-back,
        .twt-signin-modal .twt-modal-back,
        .twt-signin-modal .whop-oauth-ghost,
        .twt-signin-modal .whop-oauth-resend-btn,
        .twt-signin-modal .whop-oauth-code-wrap .whop-oauth-code-grid div {
          transition: none;
        }
      }
    `}</style>
  );
}
